import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
// FormField archetype — single text/email/tel input bound to a payload
// path. Replaces today's per-kind hardcoded field components
// (firstName/lastName/email/phone in ContactStep). Configuration:
//   - payloadKey  → dotted-path target on the draft (e.g. "contact.email")
//   - label       → field label
//   - placeholder → optional placeholder
//   - required    → blocks advance when empty
//   - validation  → "none" | "email" | "us-phone-10"
//   - autoComplete→ browser autocomplete hint
//
// Form archetypes register with the parent <CompoundStep>'s registry
// so the stage's submit handler can aggregate all field values + run
// validators in one pass.
import { useEffect, useState } from 'react';
import { useStageForm } from '../runtime/useStageForm.js';
import { s, b } from './common.js';
function readInitialValue(payload, path) {
    const parts = path.split('.');
    let cur = payload;
    for (const part of parts) {
        if (cur && typeof cur === 'object' && part in cur) {
            cur = cur[part];
        }
        else {
            return '';
        }
    }
    return typeof cur === 'string' ? cur : '';
}
function validate(value, validation, required) {
    const trimmed = value.trim();
    if (required && !trimmed)
        return 'Required';
    if (!trimmed)
        return null;
    if (validation === 'email') {
        if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(trimmed))
            return 'Please enter a valid email address.';
    }
    if (validation === 'us-phone-10') {
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length !== 10)
            return 'Please enter a 10-digit US phone number.';
    }
    return null;
}
function formatPhone(value) {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0)
        return '';
    if (digits.length < 4)
        return `(${digits}`;
    if (digits.length < 7)
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
export function FormFieldComponent(props) {
    const { config, payload, registry, blockId } = props;
    const payloadKey = s(config, 'payloadKey');
    const label = s(config, 'label');
    const placeholder = s(config, 'placeholder');
    const required = b(config, 'required', true);
    const validation = s(config, 'validation', 'none');
    const autoComplete = s(config, 'autoComplete');
    const inputType = validation === 'email' ? 'email' : validation === 'us-phone-10' ? 'tel' : 'text';
    const [value, setValue] = useState(() => readInitialValue(payload, payloadKey));
    const [touched, setTouched] = useState(false);
    useStageForm(registry, blockId, {
        payloadKey,
        value: value === '' ? undefined : validation === 'us-phone-10' ? value : value.trim(),
        validator: (v) => validate(typeof v === 'string' ? v : '', validation, required),
    });
    // Reset when the payload mutates externally (back-button into a step).
    useEffect(() => {
        const initial = readInitialValue(payload, payloadKey);
        if (initial && !value)
            setValue(initial);
        // intentional: don't depend on `value` so we don't fight the input
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payloadKey]);
    const error = touched ? validate(value, validation, required) : null;
    return (_jsxs("label", { className: "flex flex-col gap-1.5", children: [_jsxs("span", { className: "text-xs font-semibold text-[var(--bcbb-text)]", children: [label, required ? ' *' : ''] }), _jsx("input", { type: inputType, autoComplete: autoComplete || undefined, placeholder: placeholder || undefined, className: "w-full rounded-md border bg-white px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2", style: { borderColor: 'var(--bcbb-border)', color: 'var(--bcbb-text)' }, value: value, onChange: (e) => setValue(validation === 'us-phone-10' ? formatPhone(e.target.value) : e.target.value), onBlur: () => setTouched(true), required: required, inputMode: validation === 'us-phone-10' ? 'tel' : undefined, maxLength: validation === 'us-phone-10' ? 14 : undefined }), error && _jsx("span", { className: "text-xs text-[var(--bcbb-danger)]", children: error })] }));
}
export const FormFieldArchetype = {
    name: 'FormField',
    label: 'Form field',
    description: 'Single text/email/tel input bound to a payload path.',
    component: FormFieldComponent,
    isFormArchetype: true,
    fields: {
        payloadKey: { kind: 'text', label: 'Payload key (dotted path)' },
        label: { kind: 'text', label: 'Label' },
        placeholder: { kind: 'text', label: 'Placeholder', optional: true },
        required: { kind: 'checkbox', label: 'Required', defaultValue: true },
        validation: {
            kind: 'select',
            label: 'Validation',
            options: [
                { label: 'None', value: 'none' },
                { label: 'Email', value: 'email' },
                { label: 'US phone (10 digits)', value: 'us-phone-10' },
            ],
            defaultValue: 'none',
        },
        autoComplete: { kind: 'text', label: 'HTML autocomplete', optional: true },
    },
    defaultConfig: {
        payloadKey: '',
        label: 'Field',
        placeholder: '',
        required: true,
        validation: 'none',
        autoComplete: '',
    },
};
